import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { 
  Scissors, Plus, Edit2, Trash2, Save, X, 
  DollarSign, Clock, CheckCircle, AlertCircle,
  Loader2, Palette, ImagePlus
} from 'lucide-react';
import api from '../utils/api';
import { EmptyState, InlineAlert, LoadingState, PageHeader, SectionCard } from '../components/ui';
import { useToast } from '../components/ToastProvider';

export default function ServicesManager() {
  const { id } = useParams();
  const toast = useToast();
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingService, setEditingService] = useState(null);
  const [creatingService, setCreatingService] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    loadServices();
  }, [id]);

  const loadServices = async () => {
    try {
      const response = await api.get(`/barbershops/${id}/services`);
      setServices(response.data.services || []);
    } catch (err) {
      console.error('Erro ao carregar serviços:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    setSuccess('');

    try {
      const response = await api.post(`/barbershops/${id}/services`, {
        nome: creatingService.nome,
        preco: parseFloat(creatingService.preco),
        duracao_minutos: parseInt(creatingService.duracao_minutos),
        cor: creatingService.cor,
        image_url: creatingService.image_url || null,
        image_path: creatingService.image_path || null,
        order_bump_enabled: !!creatingService.order_bump_enabled,
        order_bump_label: creatingService.order_bump_label || null
      });

      setServices([...services, response.data.service]);
      setCreatingService(false);
      setSuccess('Servico criado com sucesso!');
      toast.success('Servico criado com sucesso.');
    } catch (err) {
      setError(err.response?.data?.error || 'Erro ao criar serviço');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    setSuccess('');

    try {
      await api.put(`/barbershops/${id}/services/${editingService.id}`, {
        nome: editingService.nome,
        preco: parseFloat(editingService.preco),
        duracao_minutos: parseInt(editingService.duracao_minutos),
        cor: editingService.cor,
        image_url: editingService.image_url || null,
        image_path: editingService.image_path || null,
        order_bump_enabled: !!editingService.order_bump_enabled,
        order_bump_label: editingService.order_bump_label || null
      });

      setServices(services.map(s => 
        s.id === editingService.id ? { ...s, ...editingService } : s
      ));
      setEditingService(null);
      setSuccess('Servico atualizado com sucesso!');
      toast.success('Servico atualizado com sucesso.');
    } catch (err) {
      setError(err.response?.data?.error || 'Erro ao atualizar serviço');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (serviceId) => {
    if (!confirm('Tem certeza que deseja excluir este serviço?')) return;

    try {
      await api.delete(`/barbershops/${id}/services/${serviceId}`);
      setServices(services.filter(s => s.id !== serviceId));
      setSuccess('Servico excluido com sucesso!');
      toast.success('Servico excluido com sucesso.');
    } catch (err) {
      setError(err.response?.data?.error || 'Erro ao excluir serviço');
    }
  };

  const startEdit = (service) => {
    setEditingService({...service});
  };

  const cancelEdit = () => {
    setEditingService(null);
    setCreatingService(false);
    setError('');
    setSuccess('');
  };

  const colors = [
    '#7c3aed', '#3b82f6', '#10b981', '#f59e0b', 
    '#ef4444', '#ec4899', '#8b5cf6', '#06b6d4'
  ];

  const getImageUrl = (url) => {
    if (!url) return null;
    return url.startsWith('http') ? url : `${import.meta.env.VITE_API_URL || 'http://localhost:3001'}${url}`;
  };

  const uploadServicePhoto = async (file) => {
    const formData = new FormData();
    formData.append('photo', file);
    const response = await api.post(`/upload/service-photo/${id}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    return {
      image_url: response.data.imageUrl,
      image_path: response.data.imagePath
    };
  };

  const handleCreatePhotoChange = async (file) => {
    if (!file) return;
    setSaving(true);
    setError('');
    try {
      const photo = await uploadServicePhoto(file);
      setCreatingService(prev => ({ ...prev, ...photo }));
    } catch (err) {
      setError(err.response?.data?.error || 'Erro ao enviar foto');
    } finally {
      setSaving(false);
    }
  };

  const handleEditPhotoChange = async (file) => {
    if (!file) return;
    setSaving(true);
    setError('');
    try {
      const photo = await uploadServicePhoto(file);
      setEditingService(prev => ({ ...prev, ...photo }));
    } catch (err) {
      setError(err.response?.data?.error || 'Erro ao enviar foto');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <LoadingState label="Carregando servicos..." />;
  }

  return (
    <div className="space-y-6">
      <PageHeader
        eyebrow="Catalogo"
        title="Gerenciar servicos"
        description="Organize atendimentos, fotos, precos, duracao e order bumps em um catalogo profissional."
        icon={Scissors}
        action={
          <button
            onClick={() => setCreatingService({
              nome: '',
              preco: '',
              duracao_minutos: 30,
              cor: '#7c3aed',
              image_url: null,
              image_path: null,
              order_bump_enabled: false,
              order_bump_label: ''
            })}
            className="btn-primary text-sm"
          >
            <Plus className="w-4 h-4 mr-2" />
            Novo servico
          </button>
        }
      />

      <SectionCard
        title="Servicos cadastrados"
        description={`${services.length} servico${services.length !== 1 ? 's' : ''} no catalogo.`}
        icon={Scissors}
      >
          {success && <InlineAlert type="success" className="mb-4">{success}</InlineAlert>}
          {error && <InlineAlert type="error" className="mb-4">{error}</InlineAlert>}

          {/* Lista de Serviços */}
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {services.map((service) => (
              <div key={service.id} className="card card-hover">
                {editingService?.id === service.id ? (
                  // Formulário de Edição
                  <form onSubmit={handleUpdate} className="p-4 space-y-3">
                    <div>
                      <label className="label text-xs">Nome</label>
                      <input
                        type="text"
                        required
                        className="input text-sm"
                        value={editingService.nome}
                        onChange={(e) => setEditingService({...editingService, nome: e.target.value})}
                      />
                    </div>
                    <div>
                      <label className="label text-xs">Foto</label>
                      <div className="flex items-center gap-3">
                        <div className="w-16 h-16 rounded-lg bg-dark-700 overflow-hidden flex items-center justify-center border border-dark-600">
                          {editingService.image_url ? (
                            <img src={getImageUrl(editingService.image_url)} alt={editingService.nome} className="w-full h-full object-cover" />
                          ) : (
                            <Scissors className="w-6 h-6 text-gray-500" />
                          )}
                        </div>
                        <label className="btn-ghost text-sm cursor-pointer">
                          <ImagePlus className="w-4 h-4 mr-1" />
                          Trocar
                          <input
                            type="file"
                            accept="image/*"
                            className="hidden"
                            onChange={(e) => handleEditPhotoChange(e.target.files?.[0])}
                          />
                        </label>
                        {editingService.image_url && (
                          <button
                            type="button"
                            className="btn-ghost text-sm text-red-400"
                            onClick={() => setEditingService({ ...editingService, image_url: null, image_path: null })}
                          >
                            Remover
                          </button>
                        )}
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <label className="label text-xs">Preço</label>
                        <input
                          type="number"
                          step="0.01"
                          required
                          className="input text-sm"
                          value={editingService.preco}
                          onChange={(e) => setEditingService({...editingService, preco: e.target.value})}
                        />
                      </div>
                      <div>
                        <label className="label text-xs">Minutos</label>
                        <input
                          type="number"
                          required
                          className="input text-sm"
                          value={editingService.duracao_minutos}
                          onChange={(e) => setEditingService({...editingService, duracao_minutos: e.target.value})}
                        />
                      </div>
                    </div>
                    <div>
                      <label className="label text-xs">Cor</label>
                      <div className="flex gap-2 flex-wrap">
                        {colors.map(color => (
                          <button
                            key={color}
                            type="button"
                            onClick={() => setEditingService({...editingService, cor: color})}
                            className={`w-8 h-8 rounded-lg border-2 ${
                              editingService.cor === color ? 'border-white' : 'border-transparent'
                            }`}
                            style={{ backgroundColor: color }}
                          />
                        ))}
                      </div>
                    </div>
                    <div className="rounded-lg border border-dark-700 p-3 space-y-2">
                      <label className="flex items-center gap-2 text-sm text-gray-300">
                        <input
                          type="checkbox"
                          checked={!!editingService.order_bump_enabled}
                          onChange={(e) => setEditingService({ ...editingService, order_bump_enabled: e.target.checked })}
                        />
                        Usar como order bump
                      </label>
                      {editingService.order_bump_enabled && (
                        <input
                          type="text"
                          className="input text-sm"
                          value={editingService.order_bump_label || ''}
                          onChange={(e) => setEditingService({ ...editingService, order_bump_label: e.target.value })}
                          placeholder="Ex: Adicionar barba com desconto"
                        />
                      )}
                    </div>
                    <div className="flex gap-2">
                      <button
                        type="submit"
                        disabled={saving}
                        className="btn-primary text-sm flex-1"
                      >
                        {saving ? (
                          <Loader2 className="w-4 h-4 animate-spin mx-auto" />
                        ) : (
                          <Save className="w-4 h-4 mx-auto" />
                        )}
                      </button>
                      <button
                        type="button"
                        onClick={cancelEdit}
                        className="btn-ghost text-sm"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  </form>
                ) : (
                  // Visualização Normal
                  <div className="p-4">
                    <div className="flex items-start gap-3">
                      <div 
                        className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0"
                        style={{ backgroundColor: `${service.cor}20` }}
                      >
                        {service.image_url ? (
                          <img src={getImageUrl(service.image_url)} alt={service.nome} className="w-full h-full object-cover rounded-xl" />
                        ) : (
                          <Scissors className="w-6 h-6" style={{ color: service.cor }} />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="font-medium text-white truncate">{service.nome}</h3>
                        <p className="text-sm text-gray-400">{service.duracao_minutos} minutos</p>
                        <p className="text-lg font-semibold text-primary-400">
                          R$ {service.preco.toFixed(2)}
                        </p>
                        {service.order_bump_enabled && (
                          <span className="inline-flex mt-2 text-xs px-2 py-1 rounded-full bg-yellow-500/10 text-yellow-300 border border-yellow-500/20">
                            Order bump
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="flex gap-2 mt-3">
                      <button
                        onClick={() => startEdit(service)}
                        className="btn-ghost text-sm flex-1"
                      >
                        <Edit2 className="w-4 h-4 mr-1" />
                        Editar
                      </button>
                      <button
                        onClick={() => handleDelete(service.id)}
                        className="btn-ghost text-sm text-red-400 hover:text-red-300"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>

          {services.length === 0 && !creatingService && (
            <div className="text-center py-12">
              <Scissors className="w-16 h-16 text-gray-600 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-white mb-2">Nenhum serviço cadastrado</h3>
              <p className="text-gray-400 mb-4">Comece adicionando seu primeiro serviço</p>
              <button
                onClick={() => setCreatingService({
                  nome: '',
                  preco: '',
                  duracao_minutos: 30,
                  cor: '#7c3aed',
                image_url: null,
                  image_path: null,
                  order_bump_enabled: false,
                  order_bump_label: ''
                })}
                className="btn-primary"
              >
                <Plus className="w-5 h-5 mr-2" />
                Adicionar Serviço
              </button>
            </div>
          )}
      </SectionCard>

      {/* Modal/Criação de Novo Serviço */}
      {creatingService && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="card w-full max-w-md">
            <div className="p-6">
              <h3 className="text-lg font-semibold text-white mb-4">Novo Serviço</h3>
              
              <form onSubmit={handleCreate} className="space-y-4">
                <div>
                  <label className="label">Nome do Serviço</label>
                  <input
                    type="text"
                    required
                    placeholder="Ex: Corte de Cabelo"
                    className="input"
                    value={creatingService.nome}
                    onChange={(e) => setCreatingService({...creatingService, nome: e.target.value})}
                  />
                </div>

                <div>
                  <label className="label flex items-center gap-2">
                    <ImagePlus className="w-4 h-4" />
                    Foto do serviço
                  </label>
                  <div className="flex items-center gap-3">
                    <div className="w-20 h-20 rounded-xl bg-dark-700 overflow-hidden flex items-center justify-center border border-dark-600">
                      {creatingService.image_url ? (
                        <img src={getImageUrl(creatingService.image_url)} alt={creatingService.nome || 'Serviço'} className="w-full h-full object-cover" />
                      ) : (
                        <Scissors className="w-7 h-7 text-gray-500" />
                      )}
                    </div>
                    <label className="btn-ghost cursor-pointer">
                      <ImagePlus className="w-4 h-4 mr-2" />
                      Escolher foto
                      <input
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onChange={(e) => handleCreatePhotoChange(e.target.files?.[0])}
                      />
                    </label>
                    {creatingService.image_url && (
                      <button
                        type="button"
                        onClick={() => setCreatingService({ ...creatingService, image_url: null, image_path: null })}
                        className="btn-ghost text-red-400"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="label flex items-center gap-2">
                      <DollarSign className="w-4 h-4" />
                      Preço
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      required
                      placeholder="35.00"
                      className="input"
                      value={creatingService.preco}
                      onChange={(e) => setCreatingService({...creatingService, preco: e.target.value})}
                    />
                  </div>
                  <div>
                    <label className="label flex items-center gap-2">
                      <Clock className="w-4 h-4" />
                      Duração
                    </label>
                    <input
                      type="number"
                      required
                      placeholder="30"
                      className="input"
                      value={creatingService.duracao_minutos}
                      onChange={(e) => setCreatingService({...creatingService, duracao_minutos: e.target.value})}
                    />
                  </div>
                </div>

                <div>
                  <label className="label flex items-center gap-2">
                    <Palette className="w-4 h-4" />
                    Cor
                  </label>
                  <div className="flex gap-2 flex-wrap">
                    {colors.map(color => (
                      <button
                        key={color}
                        type="button"
                        onClick={() => setCreatingService({...creatingService, cor: color})}
                        className={`w-10 h-10 rounded-lg border-2 ${
                          creatingService.cor === color ? 'border-white' : 'border-transparent'
                        }`}
                        style={{ backgroundColor: color }}
                      />
                    ))}
                  </div>
                </div>

                <div className="rounded-lg border border-dark-700 p-3 space-y-2">
                  <label className="flex items-center gap-2 text-sm text-gray-300">
                    <input
                      type="checkbox"
                      checked={!!creatingService.order_bump_enabled}
                      onChange={(e) => setCreatingService({ ...creatingService, order_bump_enabled: e.target.checked })}
                    />
                    Usar como order bump
                  </label>
                  {creatingService.order_bump_enabled && (
                    <input
                      type="text"
                      className="input"
                      value={creatingService.order_bump_label || ''}
                      onChange={(e) => setCreatingService({ ...creatingService, order_bump_label: e.target.value })}
                      placeholder="Ex: Adicionar finalização"
                    />
                  )}
                </div>

                <div className="flex gap-3">
                  <button
                    type="submit"
                    disabled={saving}
                    className="btn-primary flex-1"
                  >
                    {saving ? (
                      <>
                        <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                        Criando...
                      </>
                    ) : (
                      <>
                        <Plus className="w-5 h-5 mr-2" />
                        Criar Serviço
                      </>
                    )}
                  </button>
                  <button
                    type="button"
                    onClick={cancelEdit}
                    className="btn-ghost"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
